import React, { useState, useEffect, useMemo } from "react";
import { HiCheckCircle, HiOutlineCheckCircle } from "react-icons/hi";
import { FiCalendar, FiMail, FiPhone } from "react-icons/fi";
import { BsGenderAmbiguous } from "react-icons/bs";
import { RiFileGifFill } from "react-icons/ri";
import { LuCalendarDays } from "react-icons/lu";
import moment from "moment";
import { toast } from "react-hot-toast";
import bookingApi from "../../api/booking/BookingApi";
import Loading from "../../Util/Loading";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../Layout";
import { IoArrowBackOutline } from "react-icons/io5";
import staffApi from "../../api/user/staff.api";
import { AiFillStar } from "react-icons/ai";
import { useSelector } from "react-redux";
import ConfirmDialog from "./ConfirmDialog";

function DetailBooking() {
  const { id } = useParams();
  const navigate = useNavigate();

  const userState = useSelector((state) => state.user);

  const [booking, setBooking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [renderBookings, setRenderBookings] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch bookings from backend
  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const { response, err } = await bookingApi.getBookingById({ id });
        if (err) throw err;
        setBooking(response.data.booking);
      } catch (error) {
        console.error("Failed to load bookings:", error);
        toast.error("Could not load bookings.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchBookings();
  }, [id]);

  // console.log({ booking });

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  if (!booking) return null; // or show a spinner/loading message

  const {
    customer_info,
    selectedNailServices,
    selectedBeautyServices,
    selectedProfessional,
    selectedDateTime,
    note,
  } = booking;

  const isCancelled = booking?.booking?.status === "cancelled";
  const isPast =
    selectedDateTime && new Date(selectedDateTime).getTime() < Date.now();

  const user = booking?.user || {};

  const now = Date.now();

  const SectionCard = ({ title, children }) => (
    <div className="bg-white/10 p-4 rounded-2xl shadow border border-border">
      <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div>{children}</div>
    </div>
  );

  const renderServiceListWithTime = (services) => {
    if (!Array.isArray(services) || services.length === 0) {
      return <p className="text-base text-gray-400 italic">None selected</p>;
    }

    const totalMins = services.reduce(
      (acc, s) => acc + extractMinutes(s.desc),
      0
    );

    return (
      <>
        {services.map((item, i) => (
          <div key={i} className="flex items-start space-x-2 mb-2">
            <div className="w-6 h-6 bg-yellowlight rounded-md flex items-center justify-center mt-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-yellowdark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-white">
              {item.title}
              {item.desc && (
                <span className="text-sm text-gray-400"> ({item.desc})</span>
              )}
            </p>
          </div>
        ))}
        <p className="text-right text-yellowlight font-medium mt-2">
          Total: {formatDuration(totalMins)}
        </p>
      </>
    );
  };

  const cancelBookingHandler = () => {
    setShowConfirm(true);
  };

  const confirmCancel = async () => {
    setShowConfirm(false);
    try {
      const { response, err } = await bookingApi.cancelBooking({ id });
      if (err) throw err;
      toast.success("Booking cancelled successfully");
      navigate("/listofbooking");
    } catch (error) {
      toast.error("Failed to cancel booking");
      console.error(error);
    }
  };

  const modifyBookingHandler = () => {
    navigate(`/booking/reschedule/${id}`); // or your edit page route
  };

  const handleReactivate = async () => {
    try {
      const { response, err } = await bookingApi.reactivateBooking({ id });
      if (err) throw err;
      toast.success("Booking reactivated!");
      navigate("/listofbooking");
    } catch (error) {
      toast.error("Failed to reactivate");
      console.error(error);
    }
  };

  const extractMinutes = (desc) => {
    const lower = desc?.toLowerCase() || "";
    const hrMatch = lower.match(/(\d+)\s*hr/);
    const minMatch = lower.match(/(\d+)\s*min/);
    const hr = hrMatch ? parseInt(hrMatch[1], 10) : 0;
    const min = minMatch ? parseInt(minMatch[1], 10) : 0;
    return hr * 60 + min;
  };

  const formatDuration = (mins) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours && minutes) return `${hours} hr ${minutes} mins`;
    if (hours) return `${hours} hr`;
    return `${minutes} mins`;
  };

  const badge = "py-1 px-2 rounded-full text-xs font-semibold";

  if (loading) return <Loading />;

  return (
    <Layout>
      {showConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel this booking?"
          onConfirm={confirmCancel}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      <div className="flex items-center gap-4 ">
        <Link
          to="/listofbooking"
          className="bg-white border border-subMain border-dashed rounded-lg py-3 px-4 text-md"
        >
          <IoArrowBackOutline />
        </Link>
      </div>

      <div className="max-w-4xl mx-auto p-1 text-white space-y-6">
        <h2 className="text-2xl font-bold text-yellowlight border-b border-border pb-4 flex items-center">
          Appointment Details{" "}
          <span>
            {booking?.bookingtype === "user" && (
              <span className={`${badge} text-yellolight bg-yellow-900`}>
                Booked by Customer
              </span>
            )}
            {booking?.bookingtype === "walk-in" && (
              <span className={`${badge} text-red-500 bg-red-100`}>
                {booking?.bookingtype}
              </span>
            )}
          </span>
        </h2>

        <SectionCard title="Nail Services">
          {renderServiceListWithTime(selectedNailServices)}
        </SectionCard>

        <SectionCard title="Beauty Services">
          {renderServiceListWithTime(selectedBeautyServices)}
        </SectionCard>

        <SectionCard title="Professional">
          {selectedProfessional ? (
            <div className="flex items-center space-x-3 mt-2">
              <img
                src={selectedProfessional?.photo || "/default-avatar.png"}
                alt={selectedProfessional.name}
                className="w-10 h-10 rounded-full object-cover border border-yellowlight"
              />
              <div className="flex flex-col">
                <p className="text-lg font-semibold text-white">
                  {selectedProfessional.name}
                </p>
                {selectedProfessional.role && (
                  <p className="text-sm text-gray-400">
                    {selectedProfessional.role}
                  </p>
                )}
                <span className="flex items-center text-yellow-400 text-sm mt-1">
                  <AiFillStar className="mr-1" />
                </span>
              </div>
            </div>
          ) : (
            <p className="text-base text-gray-400 italic">
              No professional assigned
            </p>
          )}
        </SectionCard>

        <SectionCard title="Appointment Time">
          <p className="text-lg font-semibold text-white">
            {formatDateTime(selectedDateTime)}
          </p>
        </SectionCard>

        <SectionCard title="Customer Info">
          <ul className="space-y-1">
            <li>
              <span className="text-sm text-gray-400">Name:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.name || "—"}
              </span>
            </li>
            <li>
              <span className="text-sm text-gray-400">Email:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.email || "—"}
              </span>
            </li>
            <li>
              <span className="text-sm text-gray-400">Phone:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.mobile || "—"}
              </span>
            </li>
          </ul>
        </SectionCard>

        <SectionCard title="Notes">
          <p className="text-lg font-semibold text-white">
            {customer_info?.message || "—"}
          </p>
        </SectionCard>
        <div className="pt-6 border-t border-border">
          {/* {userState?.user?.email === booking?.customer_info?.email && */}
          {/* (isCancelled ? ( */}

          {isCancelled ? (
            <button
              onClick={handleReactivate}
              className="w-full px-6 py-3 bg-yellowlight hover:bg-yellowdark text-black rounded-2xl font-semibold text-lg shadow"
            >
              Reactivate Your Appointment
            </button>
          ) : isPast ? (
            <div className="bg-white/10 text-yellowlight text-sm sm:text-base p-4 rounded-2xl shadow border border-border text-center">
              <p className="font-semibold text-lg mb-1">
                Cannot rebook your appointment
              </p>
              <p>
                Sorry, the appointment date has already passed and can't be
                modified.
                <br className="mb-4" />
              </p>
              <button
                onClick={() => navigate("/")}
                className="m-3 px-6 py-3 bg-yellowlight hover:bg-yellowdark hover:text-yellowlight text-yellowdark rounded-2xl font-semibold text-lg shadow"
              >
                New Appointment
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={cancelBookingHandler}
                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-semibold text-lg shadow"
              >
                Cancel Booking
              </button>

              <button
                onClick={modifyBookingHandler}
                className="w-full px-6 py-3 bg-yellowlight hover:bg-yellowdark hover:text-yellowlight text-yellowdark rounded-2xl font-semibold text-lg shadow"
              >
                Modify Booking
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default DetailBooking;
